import React from 'react';

/* ------------------------------------------------------------------ *
 * Teve — shared UI kit (Tailwind, tw- prefixed)
 * ------------------------------------------------------------------ */

const TONES = {
  brand: 'tw-bg-brand-soft tw-text-brand',
  good: 'tw-bg-good/15 tw-text-good',
  live: 'tw-bg-live/15 tw-text-live',
  warn: 'tw-bg-warn/15 tw-text-warn',
  info: 'tw-bg-info/15 tw-text-info',
  blue: 'tw-bg-blue-500/15 tw-text-blue-600',
  neutral: 'tw-bg-ink-750 tw-text-content-muted',
};

export const Badge = ({ tone = 'neutral', children, className = '' }) => (
  <span
    className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold ${TONES[tone] || TONES.neutral} ${className}`}
  >
    {children}
  </span>
);

export const Dot = ({ tone = 'good', pulse = false }) => (
  <span
    className={`tw-inline-block tw-h-2 tw-w-2 tw-rounded-full ${
      { good: 'tw-bg-good', live: 'tw-bg-live', warn: 'tw-bg-warn', info: 'tw-bg-info', blue: 'tw-bg-blue-500', neutral: 'tw-bg-content-faint' }[tone]
    } ${pulse ? 'tw-animate-pulse' : ''}`}
  />
);

export const PageHeader = ({ title, subtitle, actions, children }) => (
  <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-start tw-gap-4">
    <div className="tw-mr-auto">
      <h1 className="tw-text-2xl tw-font-bold tw-tracking-tight tw-text-heading">{title}</h1>
      {subtitle && <p className="tw-mt-1 tw-text-sm tw-text-content-muted">{subtitle}</p>}
      {children}
    </div>
    {actions && <div className="tw-flex tw-flex-wrap tw-gap-2">{actions}</div>}
  </div>
);

const BTN = {
  primary: 'tw-bg-brand !tw-text-white hover:tw-bg-brand-hover tw-shadow-sm',
  secondary: 'tw-bg-sunken tw-text-content hover:tw-brightness-95 hover:tw-text-heading',
  ghost: 'tw-bg-transparent tw-text-content-muted hover:tw-text-heading hover:tw-bg-sunken',
  danger: 'tw-bg-live !tw-text-white hover:tw-opacity-90',
  outline: 'tw-bg-brand-soft tw-text-brand hover:tw-bg-brand hover:!tw-text-white',
};

export const Button = ({
  variant = 'primary',
  size = 'md',
  as: As = 'button',
  className = '',
  icon,
  children,
  ...rest
}) => (
  <As
    className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-[10px] tw-font-semibold tw-transition tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed ${
      size === 'sm' ? 'tw-px-3 tw-py-1.5 tw-text-xs' : size === 'lg' ? 'tw-px-5 tw-py-3 tw-text-sm' : 'tw-px-4 tw-py-2 tw-text-sm'
    } ${BTN[variant] || BTN.primary} ${className}`}
    {...rest}
  >
    {icon && <i className={`fa-solid ${icon}`} />}
    {children}
  </As>
);

export const Card = ({ title, subtitle, actions, children, className = '', bodyClass = '', noPad = false, accent = false }) => (
  <div
    className={`tw-relative tw-overflow-hidden tw-rounded-2xl tw-bg-ink-900 tw-shadow-card ${className}`}
  >
    {accent && (
      <span className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-brand/50 tw-to-transparent" />
    )}
    {(title || actions) && (
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-pt-4 tw-pb-3">
        <div className="tw-mr-auto">
          {title && <h3 className="tw-text-[0.95rem] tw-font-bold tw-tracking-tight tw-text-heading">{title}</h3>}
          {subtitle && <p className="tw-mt-0.5 tw-text-xs tw-text-content-muted">{subtitle}</p>}
        </div>
        {actions}
      </div>
    )}
    <div className={noPad ? bodyClass : `tw-px-5 tw-pb-5 ${title || actions ? 'tw-pt-0' : 'tw-pt-5'} ${bodyClass}`}>{children}</div>
  </div>
);

export const StatTile = ({ icon, label, value, tone = 'brand', hint }) => (
  <div className="tw-group tw-relative tw-overflow-hidden tw-rounded-2xl tw-bg-ink-900 tw-p-4 tw-shadow-card tw-transition hover:tw-shadow-pop">
    <div className="tw-flex tw-items-center tw-gap-3">
      <span
        className={`tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-text-[1.05rem] ${TONES[tone] || TONES.brand}`}
      >
        <i className={`fa-solid ${icon}`} />
      </span>
      <div className="tw-min-w-0">
        <div className="tw-truncate tw-text-[1.35rem] tw-font-extrabold tw-leading-tight tw-tracking-tight tw-text-heading">{value}</div>
        <div className="tw-truncate tw-text-xs tw-font-medium tw-text-content-muted">{label}</div>
      </div>
    </div>
    {hint && <div className="tw-mt-2 tw-text-xs tw-text-content-faint">{hint}</div>}
  </div>
);

export const EmptyState = ({ icon = 'fa-inbox', title, hint, action }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-14 tw-text-center">
    <div className="tw-mb-4 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-ink-800 tw-text-xl tw-text-content-faint">
      <i className={`fa-solid ${icon}`} />
    </div>
    <h3 className="tw-text-base tw-font-semibold tw-text-heading">{title}</h3>
    {hint && <p className="tw-mt-1 tw-max-w-sm tw-text-sm tw-text-content-muted">{hint}</p>}
    {action && <div className="tw-mt-4">{action}</div>}
  </div>
);

/* Simple dark table wrapper: <Table head={['A','B']}>{rows}</Table> */
export const Table = ({ head = [], children, className = '' }) => (
  <div className={`tw-overflow-x-auto ${className}`}>
    <table className="tw-w-full tw-text-sm">
      {head.length > 0 && (
        <thead>
          <tr className="tw-bg-sunken/60">
            {head.map((h, i) => (
              <th
                key={i}
                className={`tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint ${i === 0 ? 'tw-rounded-l-lg' : ''} ${i === head.length - 1 ? 'tw-rounded-r-lg' : ''}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const Tr = ({ children, onClick, className = '' }) => (
  <tr
    onClick={onClick}
    className={`tw-border-b tw-border-line-soft last:tw-border-0 tw-transition-colors ${onClick ? 'tw-cursor-pointer' : ''} hover:tw-bg-sunken/50 ${className}`}
  >
    {children}
  </tr>
);

export const Td = ({ children, className = '' }) => (
  <td className={`tw-px-4 tw-py-3 tw-align-middle tw-text-content ${className}`}>{children}</td>
);

export const Field = ({ label, hint, error, children, required }) => (
  <label className="tw-block">
    {label && (
      <span className="tw-mb-1.5 tw-block tw-text-sm tw-font-semibold tw-text-content">
        {label} {required && <span className="tw-text-live">*</span>}
      </span>
    )}
    {children}
    {hint && !error && <span className="tw-mt-1 tw-block tw-text-xs tw-text-content-faint">{hint}</span>}
    {error && <span className="tw-mt-1 tw-block tw-text-xs tw-text-live">{error}</span>}
  </label>
);

export const inputClass =
  'tw-block tw-w-full tw-rounded-[10px] tw-border tw-border-transparent tw-bg-sunken tw-px-3.5 tw-py-2.5 tw-text-sm tw-text-content placeholder:tw-text-content-faint tw-transition focus:tw-border-brand focus:tw-bg-surface focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-ring';

export const Input = React.forwardRef(({ className = '', ...rest }, ref) => (
  <input ref={ref} className={`${inputClass} ${className}`} {...rest} />
));

export const Select = React.forwardRef(({ children, className = '', ...rest }, ref) => (
  <select ref={ref} className={`${inputClass} ${className}`} {...rest}>
    {children}
  </select>
));

export const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="tw-inline-flex tw-items-center tw-gap-2.5"
  >
    <span
      className={`tw-relative tw-h-5 tw-w-9 tw-rounded-full tw-transition ${checked ? 'tw-bg-brand' : 'tw-bg-content-faint/45'}`}
    >
      <span
        className={`tw-absolute tw-top-0.5 tw-h-4 tw-w-4 tw-rounded-full tw-bg-white tw-shadow-sm tw-transition ${checked ? 'tw-left-[18px]' : 'tw-left-0.5'}`}
      />
    </span>
    {label && <span className="tw-text-sm tw-text-content">{label}</span>}
  </button>
);
